import React, { useState } from "react";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import SearchBar from "../../components/SearchBar";
import PosterTile from "../../components/PosterTile";
import {
  currentStandoutYear,
  useStandoutCandidates,
} from "../../hooks/useCustomQuery";
import { getContentOptionsArray } from "../../utils/utils";

/**
 * Owner's "add a standout" picker on the profile: search for anything, with the
 * things they've already finished (with a date) this year offered underneath —
 * the likeliest picks, and ones that won't need a finish date prompt.
 * onPick(contentId, title) hands the choice to StandoutAddFlow.
 *
 * From the profile's All view (contentType "all") it first asks which type to
 * add, as the mixed-collection add flow does — search needs a single type.
 */
export default function StandoutPickerDialog({
  open,
  onClose,
  contentType,
  onPick,
}) {
  const [chosenType, setChosenType] = useState(null);
  const searchType = contentType === "all" ? chosenType : contentType;
  const { data } = useStandoutCandidates(searchType, {
    enabled: open && !!searchType,
  });
  const candidates = data?.items ?? [];
  // Named up front, so it's clear what's being added before choosing a type
  const heading = `Add a ${currentStandoutYear()} standout`;

  // Ask for the type again on the next open
  const handleClose = () => {
    setChosenType(null);
    onClose();
  };
  const handlePick = (contentId, title) => {
    setChosenType(null);
    onPick(contentId, title);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      sx={{
        // Pin to the top so the search results have room to open downwards
        "& .MuiDialog-container": {
          alignItems: "flex-start",
          paddingTop: "8vh",
        },
      }}
      disableRestoreFocus={process.env.NODE_ENV === "development"}
    >
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {searchType === null ? (
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              {heading}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              What stood out for you this year?
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {getContentOptionsArray().map(
                ({ value, labelSingular, icon }) => (
                  <Chip
                    key={value}
                    icon={React.cloneElement(icon, { fontSize: "small" })}
                    label={labelSingular}
                    onClick={() => setChosenType(value)}
                    variant="outlined"
                    clickable
                    size="small"
                    sx={{ "& .MuiChip-icon": { marginLeft: "8px" } }}
                  />
                ),
              )}
            </Stack>
          </Box>
        ) : (
          <SearchBar
            contentType={searchType}
            onSelect={(contentId) => handlePick(contentId)}
            onClose={handleClose}
            enableQuickAdd={false}
            clearOnSelect={true}
            title={<>{heading}</>}
          />
        )}

        {candidates.length > 0 && (
          <Box>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Finished in {data.year}
            </Typography>
            <Grid container spacing={1.5}>
              {candidates.map((item) => (
                <Grid item xs={4} sm={3} key={item.content_id}>
                  {/* No status overlay or list actions, so a tap selects */}
                  <PosterTile
                    contentId={item.content_id}
                    contentType={item.content_type}
                    contentTitle={item.title}
                    imageUrl={item.image_url}
                    overlay={true}
                    onClick={() => handlePick(item.content_id, item.title)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
